import { useEffect, useRef } from "react";
import { Application } from "pixi.js";
import { C } from "@bd/content";
import { furnitureSprite } from "@bd/art";
import { createGame, nearestDoor, roomCost, type Company, floorSize, lots } from "@bd/sim";
import { me, useGame } from "../store";
import { StudioView, type Ghost } from "./studio";

export let studio: StudioView | null = null;

export function roomGhostValid(co: Company, type: string, x: number, y: number, w: number, h: number): { ok: boolean; why?: string } {
  const t = C.room[type];
  const lvl = C.level[co.level];
  if (!lvl.rooms.includes(type)) return { ok: false, why: `Needs a ${C.level[t.level].name.toLowerCase()}` };
  if (w < t.minW || h < t.minH) return { ok: false, why: `At least ${t.minW} × ${t.minH} tiles` };
  const fl = floorSize(co);
  if (x < 0 || y < 0 || x + w > fl.w || y + h > fl.h) return { ok: false, why: "Off the floor" };
  if (co.rooms.some((r) => x < r.x + r.w && r.x < x + w && y < r.y + r.h && r.y < y + h)) return { ok: false, why: "Overlaps a room" };
  return { ok: true };
}

export function furnitureGhostValid(co: Company, kind: string, tx: number, ty: number, rot: number): { ok: boolean; roomId?: string; why?: string } {
  const room = co.rooms.find((r) => tx >= r.x && tx < r.x + r.w && ty >= r.y && ty < r.y + r.h);
  if (!room) return { ok: false, why: "Place it inside a room" };
  const sp = furnitureSprite(kind, rot);
  const fx = tx - room.x;
  const fy = ty - room.y;
  if (fx + sp.w > room.w || fy + sp.h > room.h) return { ok: false, roomId: room.id, why: "Doesn't fit" };
  const k = C.furniture[kind];
  if (!k.floor) for (const f of room.furniture) {
    const o = C.furniture[f.kind];
    if (!o || o.floor) continue;
    const ow = f.rot % 2 ? o.h : o.w;
    const oh = f.rot % 2 ? o.w : o.h;
    if (fx < f.x + ow && f.x < fx + sp.w && fy < f.y + oh && f.y < fy + sp.h) return { ok: false, roomId: room.id, why: "Something's in the way" };
  }
  return { ok: true, roomId: room.id };
}

export function World() {
  const host = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let disposed = false;
    let ready = false;
    const app = new Application();
    let view: StudioView | null = null;
    void app.init({ preference: "webgl", antialias: false, background: "#4a4f57", resizeTo: window, resolution: 1, autoDensity: true }).then(() => {
      if (disposed) return app.destroy(true);
      ready = true;
      host.current!.appendChild(app.canvas);
      app.canvas.style.display = "block";
      app.canvas.setAttribute("data-testid", "world");
      app.canvas.setAttribute("aria-label", "Your studio");
      view = new StudioView(app);
      studio = view;
      pace();
      (window as any).__studio = view;
      const s = useGame.getState();
      view.setQuality(s.quality);
      const co = me(s.game);
      if (s.game && co) view.update(s.game, co);
      else titleBackdrop(view);
    });
    const unsub = useGame.subscribe((s, prev) => {
      if (!view) return;
      const co = me(s.game);
      if (s.game && !prev.game) view.dayNight = true;
      if (s.game && co && (s.game !== prev.game || !view.scene)) view.update(s.game, co);
      // leaving the title: frame your own studio (the backdrop was nudged aside for the card)
      if (s.game && !prev.game) view.fit();
      if (s.quality !== prev.quality) view.setQuality(s.quality);
      view.over.selectedRoom = s.selectedRoom;
      // the studio reacts out loud to what just happened
      if (s.game && prev.game && s.game !== prev.game && co) {
        // lasting indexes: the front of the log is trimmed once a year (logBase counts what went)
        const from = (prev.game.logBase ?? 0) + prev.game.log.length;
        for (const e of s.game.log.slice(Math.max(0, from - (s.game.logBase ?? 0)))) {
          if (e.company !== co.id) continue;
          const kind = e.kind === "release" ? (/\b(8|9)\.\d/.test(e.text) ? "hit" : /\b[0-4]\.\d/.test(e.text) ? "flop" : "release") : e.kind;
          const who = co.staff.find((x) => e.text.startsWith(x.name))?.id;
          // a new phase clears what people were saying about the old one
          if (e.kind === "phase") view.clearBubbles();
          view.bark(e.kind === "phase" && e.text.endsWith("ready to print.") ? "ready" : kind, who);
        }
      }
      // placement ended (built, cancelled, tool closed): no outline left behind
      if (!s.placing && view.over.ghost) view.over.ghost = null;
      if (s.placing !== prev.placing) roomLocked = false;
    });
    const onResize = () => view?.resize();
    window.addEventListener("resize", onResize);

    // ---------------------------------------------------------------- frame pacing: easy on the GPU
    // pixel art walks at a few frames a second, so 30 fps is plenty; 60 only while you move the camera
    let moving = false;
    let focused = document.hasFocus();
    const pace = () => {
      if (!ready) return;
      const playing = useGame.getState().speed > 0;
      app.ticker.maxFPS = !focused ? 5 : moving ? 60 : playing ? 30 : 15;
    };
    const unsubPace = useGame.subscribe((s, prev) => {
      if (s.speed !== prev.speed) pace();
    });
    const onFocus = () => {
      focused = true;
      pace();
    };
    const onBlur = () => {
      focused = false;
      pace();
    };
    const onMoveStart = () => {
      moving = true;
      pace();
    };
    const onMoveEnd = () => {
      moving = false;
      pace();
    };
    window.addEventListener("focus", onFocus);
    window.addEventListener("blur", onBlur);
    window.addEventListener("pointerdown", onMoveStart);
    window.addEventListener("pointerup", onMoveEnd);
    let wheelT = 0;
    const onWheelPace = () => {
      onMoveStart();
      clearTimeout(wheelT);
      wheelT = window.setTimeout(onMoveEnd, 400);
    };
    window.addEventListener("wheel", onWheelPace, { passive: true });

    // ---------------------------------------------------------------- input
    let drag: { x: number; y: number; moved: boolean; tile: { tx: number; ty: number } | null; button: number } | null = null;
    // once a room outline is dropped it stays put while the player moves to its Build button
    let roomLocked = false;
    const el = host.current!;
    const local = (e: PointerEvent | WheelEvent) => {
      const r = el.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    const onDown = (e: PointerEvent) => {
      if (!view) return;
      const p = local(e);
      const t = view.screenToTile(p.x, p.y);
      drag = { x: p.x, y: p.y, moved: false, tile: t ? { tx: t.tx, ty: t.ty } : null, button: e.button };
      if (useGame.getState().placing?.kind === "room" && e.button === 0) roomLocked = false;
      el.setPointerCapture(e.pointerId);
    };
    const onMove = (e: PointerEvent) => {
      if (!view) return;
      const s = useGame.getState();
      const co = me(s.game);
      const p = local(e);
      const t = view.screenToTile(p.x, p.y);
      view.hoverRoom = t ? view.roomAt(t.tx, t.ty)?.id ?? null : null;
      const placing = s.placing;
      if (placing?.kind === "room" && co && t && !roomLocked) {
        const a = drag?.tile && !drag.button ? drag.tile : { tx: t.tx, ty: t.ty };
        const def = C.room[placing.type];
        const x = drag?.tile && !drag.button ? Math.min(a.tx, t.tx) : t.tx;
        const y = drag?.tile && !drag.button ? Math.min(a.ty, t.ty) : t.ty;
        const w = drag?.tile && !drag.button ? Math.max(def.minW, Math.abs(t.tx - a.tx) + 1) : def.minW;
        const h = drag?.tile && !drag.button ? Math.max(def.minH, Math.abs(t.ty - a.ty) + 1) : def.minH;
        const v = roomGhostValid(co, placing.type, x, y, w, h);
        view.over.ghost = { kind: "room", type: placing.type, x, y, w, h, valid: v.ok };
        useGame.setState({ buildGhost: { type: placing.type, x, y, w, h, valid: v.ok, price: roomCost(def, w, h, s.game!.week), why: v.why, screen: view.toScreen((x + w) * 6, (y + h / 2) * 6) } });
      } else if (placing?.kind === "door" && co && t) {
        const room = co.rooms.find((r) => r.id === placing.roomId);
        if (room) {
          const d = nearestDoor(room, t.tx, t.ty);
          view.over.ghost = { kind: "furniture", type: "door", x: d.x, y: d.y, w: 1, h: 1, valid: true };
        }
      } else if (placing?.kind === "furniture" && co && t) {
        const rot = useGame.getState().rot;
        const v = furnitureGhostValid(co, placing.type, t.tx, t.ty, rot);
        view.over.ghost = { kind: "furniture", type: placing.type, x: t.tx, y: t.ty, w: 1, h: 1, rot, valid: v.ok } as Ghost;
      }
      if (drag && !placing) {
        const dx = p.x - drag.x;
        const dy = p.y - drag.y;
        if (Math.abs(dx) + Math.abs(dy) > 4) drag.moved = true;
        if (drag.moved) {
          view.pan(dx, dy);
          drag.x = p.x;
          drag.y = p.y;
        }
      }
    };
    const onUp = async (e: PointerEvent) => {
      if (!view) return;
      const s = useGame.getState();
      const co = me(s.game);
      const p = local(e);
      const t = view.screenToTile(p.x, p.y);
      const placing = s.placing;
      const d = drag;
      drag = null;
      if (placing?.kind === "room") {
        // the outline stays where it was dropped; the price card (BuildCard) confirms
        if (d && d.button === 0 && useGame.getState().buildGhost) roomLocked = true;
        return;
      }
      if (placing?.kind === "door" && co && t) {
        const room = co.rooms.find((r) => r.id === placing.roomId);
        if (room) {
          const d = nearestDoor(room, t.tx, t.ty);
          const res = await s.cmd({ type: "setDoor", roomId: room.id, x: d.x, y: d.y });
          if (!res.ok) s.toast({ title: "Can't put a door there", text: res.error, tone: "warn" });
        }
        useGame.setState({ placing: null });
        view.over.ghost = null;
        return;
      }
      if (placing?.kind === "furniture" && co && t) {
        const rot = useGame.getState().rot;
        const v = furnitureGhostValid(co, placing.type, t.tx, t.ty, rot);
        if (!v.ok || !v.roomId) {
          s.toast({ title: "Can't place that here", text: v.why, tone: "warn" });
          return;
        }
        const room = co.rooms.find((r) => r.id === v.roomId)!;
        const res = await s.cmd({ type: "placeFurniture", roomId: room.id, kind: placing.type, x: t.tx - room.x, y: t.ty - room.y, rot });
        if (!res.ok) s.toast({ title: "Can't place that", text: res.error, tone: "warn" });
        return;
      }
      if (d && !d.moved && t) {
        const room = view.roomAt(t.tx, t.ty);
        // a building next door that's for sale: show what buying it gives you
        const lot = !room && co ? lots(co).find((l) => !l.owned && t.tx >= l.x && t.tx < l.x + l.w && t.ty >= l.y - 2 && t.ty < l.y + l.h) : undefined;
        useGame.setState({ selectedRoom: room?.id ?? null, selectedLot: lot?.kind ?? null });
      }
    };
    const onWheel = (e: WheelEvent) => {
      if (!view) return;
      e.preventDefault();
      view.zoom(e.deltaY < 0 ? 1 : -1, local(e));
    };
    el.addEventListener("pointerdown", onDown);
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", onUp);
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      disposed = true;
      unsub();
      unsubPace();
      clearTimeout(wheelT);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("pointerdown", onMoveStart);
      window.removeEventListener("pointerup", onMoveEnd);
      window.removeEventListener("wheel", onWheelPace);
      el.removeEventListener("pointerdown", onDown);
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
      el.removeEventListener("wheel", onWheel);
      studio = null;
      if (ready) app.destroy(true);
    };
  }, []);
  return <div ref={host} style={{ position: "fixed", inset: 0, touchAction: "none" }} />;
}

/** Behind the title card: a garage in this season, at dusk, with the light on and the town going by. */
function titleBackdrop(view: StudioView) {
  const month = new Date().getMonth();
  const demo = createGame({ seed: "title-backdrop", players: [{ id: "you", company: "Box & Dice Co." }], mode: "solo" });
  // the season you're playing in, outside your window
  demo.week = Math.floor(((month + 10) % 12) / 3) * 13 + 6;
  view.dayNight = false;
  view.update(demo, demo.companies[0]);
  view.setMood("dusk");
  // nudge the garage left of centre on wide screens, clear of the card
  if (window.innerWidth >= 1100) view.panBy(window.innerWidth * 0.18, 0);
}
